// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

// cc-common imports
import { IconConstants } from 'cc-common';

// RXJS imports
import { Subscription } from 'rxjs';

// 3rd Party imports
import notify from 'devextreme/ui/notify';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';

// Service imports
import { OwnerWelcomeLetterService } from '../_services/owner-welcome-letter.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';

// model imports
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { OwnerWelcomeLetterEditorResultDto } from '../_models/owner-welcome-letter/owner-welcome-letter-editor-result-dto';
import { OwnerWelcomeLetterParameterDto } from '../_models/owner-welcome-letter/owner-welcome-letter-parameter-dto';
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';

@Component({
    selector: 'app-owner-welcome-letter',
    templateUrl: './owner-welcome-letter.component.html',
    styleUrls: ['./owner-welcome-letter.component.scss']
})
export class OwnerWelcomeLetterComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Output() closeWelcomeLetter = new EventEmitter<boolean>();

    loadingVisible = false;
    sendByMail = false;
    sendByEmail = false;
    contextChangeSubscription: Subscription;

    selectedContext: PropertyCommunityInfoResultDto;
    welcomeLettorEditorData: OwnerWelcomeLetterEditorResultDto;

    iconCancel = IconConstants.CANCEL;
    iconSearch = IconConstants.SEARCH;
    iconCheck = IconConstants.CHECK;

    constructor(
        private readonly ownerWelcomeLetterService: OwnerWelcomeLetterService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly contextService: ContextManagerService
    ) {}

    ngOnInit() {
        this.updateSelectedContext();
        this.contextChangeSubscription = this.contextService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });
    }

    ngOnDestroy(): void {
        this.contextChangeSubscription.unsubscribe();
    }

    onHidden(e): void {
        this.visible = false;
        this.closeWelcomeLetter.emit(false);
    }

    onSubmitClick(): void {
        const param: OwnerWelcomeLetterParameterDto = new OwnerWelcomeLetterParameterDto();
        param.communityId = this.selectedContext.communityId;
        param.customerId = this.selectedContext.customerId;
        param.sendByMail = this.sendByMail;
        param.sendByEmail = this.sendByEmail;
        param.clearCache = true;
        this.loadingVisible = true;
        this.ownerWelcomeLetterService.generateWelcomeLetter(param).subscribe(
            (result: StatusMessageResultDto) => {
                if (result.isSuccessMessage) {
                    notify(result.message, 'Success', MessageInterval.successMsgInterval);
                } else {
                    notify(result.message, 'Error', MessageInterval.errorMsgInterval);
                }
                this.onHidden(false);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onPreviewClick(): void {
        const param: OwnerWelcomeLetterParameterDto = new OwnerWelcomeLetterParameterDto();
        param.communityId = this.selectedContext.communityId;
        param.customerId = this.selectedContext.customerId;
        param.clearCache = true;
        this.loadingVisible = true;
        this.ownerWelcomeLetterService.getWelcomeLetterPreview(param).subscribe(
            (file: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(file);
                this.helperService.openWindow(fileURL, '_blank', null, null);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateWelcomeLetterEditor(clearCache: boolean): void {
        const param: OwnerWelcomeLetterParameterDto = new OwnerWelcomeLetterParameterDto();
        param.communityId = this.selectedContext.communityId;
        param.customerId = this.selectedContext.customerId;
        param.clearCache = clearCache;
        this.loadingVisible = true;
        this.ownerWelcomeLetterService.getWelcomeLetterEditor(param).subscribe(
            (result) => {
                this.welcomeLettorEditorData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextService.getSelectedContext();
        if (!!this.selectedContext) {
            this.populateWelcomeLetterEditor(true);
        }
    }
}
